/**
 * zustand `persist` storage that can never throw out of `setItem`.
 *
 * The default `createJSONStorage(() => localStorage)` propagates two errors
 * straight into whatever `set()` call triggered the write: `RangeError:
 * Invalid string length` (the serialized state passed V8's maximum string
 * length) and `QuotaExceededError` (the ~10 MB per-origin localStorage
 * quota). Both were uncaught renderer crashes on 1.4.9–1.4.25 for the
 * session, query-history and tab-history stores — every store that persists
 * SQL bodies. This wrapper swallows the failure, keeps the last good snapshot
 * on disk, and reports it once per (store, reason) per session so the
 * dashboard can see which store outgrew its cap.
 */

import { createJSONStorage, type PersistStorage, type StorageValue } from 'zustand/middleware';

export type PersistWriteFailureReason = 'quota' | 'string_length' | 'other';

/** Coarse size bucket of the write that failed (characters of JSON, when known). */
function sizeBucket(chars: number | null): string {
  if (chars === null) return 'unknown';
  const mb = 1024 * 1024;
  if (chars <= 1 * mb) return '<=1mb';
  if (chars <= 5 * mb) return '1-5mb';
  if (chars <= 10 * mb) return '5-10mb';
  if (chars <= 100 * mb) return '10-100mb';
  return '>100mb';
}

export function classifyPersistWriteError(error: unknown): PersistWriteFailureReason {
  if (error instanceof RangeError || (error instanceof Error && /Invalid string length/i.test(error.message))) {
    return 'string_length';
  }
  const name = typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : undefined;
  const code = typeof error === 'object' && error !== null ? (error as { code?: unknown }).code : undefined;
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED' || code === 22 || code === 1014) {
    return 'quota';
  }
  if (error instanceof Error && /quota/i.test(error.message)) return 'quota';
  return 'other';
}

const reportedFailures = new Set<string>();

/** Test hook: forget which (store, reason) pairs were already reported. */
export function resetPersistWriteFailureReports(): void {
  reportedFailures.clear();
}

/** Returns true the first time a (store, reason) pair fails in this session. */
function claimFirstFailure(storeName: string, reason: PersistWriteFailureReason): boolean {
  const key = `${storeName}|${reason}`;
  if (reportedFailures.has(key)) return false;
  reportedFailures.add(key);
  return true;
}

/**
 * Measure the failed payload. Only for bounded cases — for `string_length`
 * the measurement is the very operation that threw.
 */
function measurePayload<S>(value: StorageValue<S>, reason: PersistWriteFailureReason): number | null {
  if (reason === 'string_length') return null;
  try {
    return JSON.stringify(value).length;
  } catch {
    return null;
  }
}

function reportPersistWriteFailure<S>(
  storeName: string,
  options: GuardedJsonStorageOptions,
  reason: PersistWriteFailureReason,
  value: StorageValue<S>,
): void {
  // Nothing in here may throw back into zustand's `set` — that is the very
  // crash this module exists to stop. `persist` also calls `setItem` during
  // hydration, before React (and the toaster) has mounted.
  try {
    console.warn(`[persist] write to '${storeName}' dropped (${reason}); the last saved snapshot is kept.`);
  } catch {
    // ignore
  }
  try {
    // The user must learn that saving stopped — the old behaviour was a
    // crash, which was at least visible. Once per (store, reason) per session.
    options.onFailure?.(reason);
  } catch {
    // A notice that fails must not turn a dropped write into a crash.
  }
  if (typeof window === 'undefined') return;
  window.electronAPI?.analytics
    ?.track('app:persist_write_failed', {
      store: storeName,
      reason,
      sizeBucket: sizeBucket(measurePayload(value, reason)),
    })
    .catch(() => {});
}

export type GuardedJsonStorageOptions = {
  /**
   * Called once per (store, reason) per session when a write is dropped —
   * the store owner shows the user-facing notice (core stays UI-free).
   */
  onFailure?: (reason: PersistWriteFailureReason) => void;
};

/**
 * Build the guarded storage. Returns `undefined` (zustand then skips
 * persistence, its own convention) when `localStorage` is unavailable.
 */
export function createGuardedJsonStorage<S>(options: GuardedJsonStorageOptions = {}): PersistStorage<S> | undefined {
  const base = createJSONStorage<S>(() => localStorage);
  if (!base) return undefined;
  return {
    getItem: (name) => base.getItem(name),
    removeItem: (name) => base.removeItem(name),
    setItem: (name: string, value: StorageValue<S>) => {
      try {
        return base.setItem(name, value);
      } catch (error) {
        const reason = classifyPersistWriteError(error);
        // Dedupe BEFORE any measurement: a stalled auto-save retries every
        // few hundred ms and must not re-stringify a multi-MB payload each time.
        if (claimFirstFailure(name, reason)) {
          reportPersistWriteFailure(name, options, reason, value);
        }
        return undefined;
      }
    },
  };
}
